import React from 'react';
import ReactMarkdown from 'react-markdown';

const NewsSection = ({ publicNews, privateNews, isAuthenticated, userInfo, darkMode }) => {
  // Don't render anything if both sections are disabled
  if (!publicNews?.enabled && !privateNews?.enabled) {
    return null;
  }

  // Function to personalize content by replacing placeholders
  const personalizeContent = (content) => {
    if (!content || !userInfo) {
      return content;
    }

    let personalizedContent = content;
    
    // Replace <First_Name> with actual first name
    if (userInfo.first_name) {
      personalizedContent = personalizedContent.replace(/<First_Name>/g, userInfo.first_name);
    }
    
    // Can add more personalizations here in the future:
    // personalizedContent = personalizedContent.replace(/<Last_Name>/g, userInfo.last_name);
    // personalizedContent = personalizedContent.replace(/<Email>/g, userInfo.email);
    
    return personalizedContent;
  };

  // Safe markdown configuration
  const markdownConfig = {
    allowedElements: ['p', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'br'],
    urlTransform: (uri) => {
      // Only allow http/https links and internal routes
      if (uri.startsWith('http://') || uri.startsWith('https://') || uri.startsWith('/')) {
        return uri;
      }
      return null;
    },
    components: {
      // Custom link component to handle target behavior
      a: ({ href, children, ...props }) => {
        const isExternal = href && (href.startsWith('http://') || href.startsWith('https://'));
        return (
          <a
            href={href}
            target={isExternal ? '_blank' : '_self'}
            rel={isExternal ? 'noopener noreferrer' : undefined}
            {...props}
          >
            {children}
          </a>
        );
      }
    }
  };

  const renderNewsCard = (news, type) => {
    if (!news?.enabled || !news?.title) return null;

    // Apply personalization to private section content
    const titleContent = type === 'private' ? personalizeContent(news.title) : news.title;
    const descriptionContent = type === 'private' ? personalizeContent(news.description) : news.description;

    return (
      <div 
        key={type}
        className={`rounded-2xl p-6 shadow-lg border mb-4 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}
      >
        {/* News Title */}
        <div className={`text-xl font-bold mb-3 ${
          darkMode ? 'text-white' : 'text-gray-900'
        }`}>
          <ReactMarkdown {...markdownConfig}>
            {titleContent}
          </ReactMarkdown>
        </div>

        {/* News Description */}
        {descriptionContent && (
          <div className={`prose prose-lg max-w-none ${
            darkMode 
              ? 'prose-invert prose-purple prose-a:text-purple-400 prose-strong:text-white prose-em:text-gray-300' 
              : 'prose-gray prose-a:text-purple-600 prose-strong:text-gray-900'
          }`}>
            <ReactMarkdown {...markdownConfig}>
              {descriptionContent}
            </ReactMarkdown>
          </div>
        )}

        {/* Section indicator */}
        <div className={`mt-4 text-xs font-medium ${
          type === 'private' 
            ? (darkMode ? 'text-green-400' : 'text-green-600')
            : (darkMode ? 'text-blue-400' : 'text-blue-600')
        }`}>
          {type === 'private' ? '🔒 Contenido para miembros' : '📢 Anuncio público'}
        </div>
      </div>
    );
  };

  return (
    <div className="mb-8">
      {/* Private section first (for logged-in users) */}
      {isAuthenticated && renderNewsCard(privateNews, 'private')}
      
      {/* Public section (for all users) */}
      {renderNewsCard(publicNews, 'public')}
    </div>
  );
};

export default NewsSection;